/**
 * Settings Zustand Store
 *
 * Manages all state for the Platform Settings section including:
 * - Email configuration
 * - SMS configuration
 * - Compliance settings
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  EmailConfig,
  SMSConfig,
  ComplianceSettings,
  UpdateEmailConfigDto,
  UpdateSMSConfigDto,
  UpdateComplianceSettingsDto,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface SettingsError {
  code: string;
  message: string;
}

// ============================================
// Mock Data (until backend is ready)
// ============================================

const MOCK_EMAIL_CONFIG: EmailConfig = {
  provider: 'mailgun',
  apiKey: '****************************',
  domain: 'mail.esigforms.com',
  fromEmail: 'noreply@esigforms.com',
  fromName: 'ESigForms',
  replyToEmail: 'support@esigforms.com',
  smtpHost: null,
  smtpPort: null,
  smtpUsername: null,
  smtpPassword: null,
  smtpSecure: null,
  trackOpens: true,
  trackClicks: true,
  updatedAt: new Date('2024-01-15'),
};

const MOCK_SMS_CONFIG: SMSConfig = {
  provider: 'sinch',
  apiKey: '****************************',
  apiSecret: '****************************',
  senderId: 'ESigForms',
  servicePlanId: 'sp-12345',
  accountSid: null,
  authToken: null,
  messagingServiceSid: null,
  deliveryReports: true,
  optOutHandling: true,
  updatedAt: new Date('2024-01-15'),
};

const MOCK_COMPLIANCE_SETTINGS: ComplianceSettings = {
  dataRetentionDays: 365,
  auditLogRetentionDays: 730,
  gdprEnabled: true,
  hipaaEnabled: false,
  ccpaEnabled: true,
  anonymizeDeletedUsers: true,
  requireConsentForMarketing: true,
  enforcePasswordPolicy: true,
  passwordMinLength: 12,
  passwordRequireUppercase: true,
  passwordRequireNumber: true,
  passwordRequireSpecial: true,
  sessionTimeoutMinutes: 60,
  maxFailedLoginAttempts: 5,
  lockoutDurationMinutes: 15,
  logAllApiCalls: false,
  logSensitiveDataAccess: true,
  updatedAt: new Date('2024-02-01'),
};

// ============================================
// State Interface
// ============================================

export interface SettingsState {
  // Email Config
  emailConfig: EmailConfig | null;
  isLoadingEmail: boolean;
  isSavingEmail: boolean;
  emailError: SettingsError | null;

  // SMS Config
  smsConfig: SMSConfig | null;
  isLoadingSMS: boolean;
  isSavingSMS: boolean;
  smsError: SettingsError | null;

  // Compliance Settings
  complianceSettings: ComplianceSettings | null;
  isLoadingCompliance: boolean;
  isSavingCompliance: boolean;
  complianceError: SettingsError | null;

  // Actions
  loadEmailConfig: () => Promise<void>;
  saveEmailConfig: (dto: UpdateEmailConfigDto) => Promise<boolean>;
  loadSMSConfig: () => Promise<void>;
  saveSMSConfig: (dto: UpdateSMSConfigDto) => Promise<boolean>;
  loadComplianceSettings: () => Promise<void>;
  saveComplianceSettings: (dto: UpdateComplianceSettingsDto) => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Email Config
  emailConfig: null as EmailConfig | null,
  isLoadingEmail: false,
  isSavingEmail: false,
  emailError: null as SettingsError | null,

  // SMS Config
  smsConfig: null as SMSConfig | null,
  isLoadingSMS: false,
  isSavingSMS: false,
  smsError: null as SettingsError | null,

  // Compliance Settings
  complianceSettings: null as ComplianceSettings | null,
  isLoadingCompliance: false,
  isSavingCompliance: false,
  complianceError: null as SettingsError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useSettingsStore = create<SettingsState>((set) => ({
  ...getInitialState(),

  // ============================================
  // Email Config Actions
  // ============================================

  loadEmailConfig: async () => {
    set({ isLoadingEmail: true, emailError: null });
    try {
      const api = getApiClient();
      const response = await api.get<{
        success: boolean;
        data: EmailConfig;
      }>(API_ENDPOINTS.SUPER_ADMIN.SETTINGS.EMAIL);

      set({
        emailConfig: response.data,
        isLoadingEmail: false,
      });
    } catch {
      // Use mock data when API is not available
      set({
        emailConfig: MOCK_EMAIL_CONFIG,
        isLoadingEmail: false,
      });
    }
  },

  saveEmailConfig: async (dto: UpdateEmailConfigDto): Promise<boolean> => {
    set({ isSavingEmail: true, emailError: null });
    try {
      const api = getApiClient();
      const response = await api.put<{
        success: boolean;
        data: EmailConfig;
      }>(API_ENDPOINTS.SUPER_ADMIN.SETTINGS.EMAIL, dto);

      set({
        emailConfig: response.data,
        isSavingEmail: false,
      });
      return true;
    } catch (error) {
      set({
        isSavingEmail: false,
        emailError: {
          code: 'SAVE_EMAIL_CONFIG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to save email configuration',
        },
      });
      return false;
    }
  },

  // ============================================
  // SMS Config Actions
  // ============================================

  loadSMSConfig: async () => {
    set({ isLoadingSMS: true, smsError: null });
    try {
      const api = getApiClient();
      const response = await api.get<{
        success: boolean;
        data: SMSConfig;
      }>(API_ENDPOINTS.SUPER_ADMIN.SETTINGS.SMS);

      set({
        smsConfig: response.data,
        isLoadingSMS: false,
      });
    } catch {
      // Use mock data when API is not available
      set({
        smsConfig: MOCK_SMS_CONFIG,
        isLoadingSMS: false,
      });
    }
  },

  saveSMSConfig: async (dto: UpdateSMSConfigDto): Promise<boolean> => {
    set({ isSavingSMS: true, smsError: null });
    try {
      const api = getApiClient();
      const response = await api.put<{
        success: boolean;
        data: SMSConfig;
      }>(API_ENDPOINTS.SUPER_ADMIN.SETTINGS.SMS, dto);

      set({
        smsConfig: response.data,
        isSavingSMS: false,
      });
      return true;
    } catch (error) {
      set({
        isSavingSMS: false,
        smsError: {
          code: 'SAVE_SMS_CONFIG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to save SMS configuration',
        },
      });
      return false;
    }
  },

  // ============================================
  // Compliance Settings Actions
  // ============================================

  loadComplianceSettings: async () => {
    set({ isLoadingCompliance: true, complianceError: null });
    try {
      const api = getApiClient();
      const response = await api.get<{
        success: boolean;
        data: ComplianceSettings;
      }>(API_ENDPOINTS.SUPER_ADMIN.SETTINGS.COMPLIANCE);

      set({
        complianceSettings: response.data,
        isLoadingCompliance: false,
      });
    } catch {
      // Use mock data when API is not available
      set({
        complianceSettings: MOCK_COMPLIANCE_SETTINGS,
        isLoadingCompliance: false,
      });
    }
  },

  saveComplianceSettings: async (dto: UpdateComplianceSettingsDto): Promise<boolean> => {
    set({ isSavingCompliance: true, complianceError: null });
    try {
      const api = getApiClient();
      const response = await api.put<{
        success: boolean;
        data: ComplianceSettings;
      }>(API_ENDPOINTS.SUPER_ADMIN.SETTINGS.COMPLIANCE, dto);

      set({
        complianceSettings: response.data,
        isSavingCompliance: false,
      });
      return true;
    } catch (error) {
      set({
        isSavingCompliance: false,
        complianceError: {
          code: 'SAVE_COMPLIANCE_SETTINGS_FAILED',
          message:
            error instanceof Error ? error.message : 'Failed to save compliance settings',
        },
      });
      return false;
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({
      emailError: null,
      smsError: null,
      complianceError: null,
    });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Check if email is configured */
export const useIsEmailConfigured = () => {
  return useSettingsStore((state) => state.emailConfig !== null && !!state.emailConfig.apiKey);
};

/** Check if SMS is configured */
export const useIsSMSConfigured = () => {
  return useSettingsStore((state) => state.smsConfig !== null && !!state.smsConfig.apiKey);
};

/** Get email provider name */
export const useEmailProvider = () => {
  return useSettingsStore((state) => state.emailConfig?.provider ?? null);
};

/** Get SMS provider name */
export const useSMSProvider = () => {
  return useSettingsStore((state) => state.smsConfig?.provider ?? null);
};

/** Check if any settings data is loading */
export const useSettingsLoading = () => {
  const isLoadingEmail = useSettingsStore((state) => state.isLoadingEmail);
  const isLoadingSMS = useSettingsStore((state) => state.isLoadingSMS);
  const isLoadingCompliance = useSettingsStore((state) => state.isLoadingCompliance);
  return isLoadingEmail || isLoadingSMS || isLoadingCompliance;
};

/** Check if any settings are being saved */
export const useSettingsSaving = () => {
  const isSavingEmail = useSettingsStore((state) => state.isSavingEmail);
  const isSavingSMS = useSettingsStore((state) => state.isSavingSMS);
  const isSavingCompliance = useSettingsStore((state) => state.isSavingCompliance);
  return isSavingEmail || isSavingSMS || isSavingCompliance;
};

/** Get GDPR compliance status */
export const useIsGDPREnabled = () => {
  return useSettingsStore((state) => state.complianceSettings?.gdprEnabled ?? false);
};

/** Get HIPAA compliance status */
export const useIsHIPAAEnabled = () => {
  return useSettingsStore((state) => state.complianceSettings?.hipaaEnabled ?? false);
};
